/**
 * College Favorites Service
 * CRUD operations for student favorites/shortlist and application hearts
 */

import { supabase } from "../../lib/supabase";
import type { HeartedApplication } from "./types";
import { getErrorMessage } from "../../utils/errorUtils";

// ============================================
// Helper Functions
// ============================================

/**
 * Get student ID from students table using auth user ID
 * @param authUserId - UUID from auth.users (session.user.id)
 * @returns Student ID from students table
 * @throws Error if student profile not found
 */
export async function getStudentIdFromAuth(
  authUserId: string,
): Promise<string> {
  const { data, error } = await supabase
    .from("students")
    .select("id")
    .eq("auth_user_id", authUserId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching student profile:", error);
    throw new Error(`Failed to fetch student profile: ${error.message}`);
  }

  if (!data) {
    throw new Error(
      "Student profile not found. Please contact your administrator to create a student profile.",
    );
  }

  return data.id;
}

/**
 * Clean and validate college ID
 */
function cleanCollegeId(collegeId: string): number {
  if (!collegeId || typeof collegeId !== "string") {
    throw new Error("College ID must be a non-empty string");
  }

  const cleanedCollegeId = collegeId.trim().replace(/[^\d]/g, "");

  if (!cleanedCollegeId) {
    throw new Error("College ID must contain at least one digit");
  }

  const collegeIdNumber = parseInt(cleanedCollegeId, 10);
  if (isNaN(collegeIdNumber)) {
    throw new Error(`Invalid college ID format: ${collegeId}`);
  }

  return collegeIdNumber;
}

// ============================================
// Favorites (Shortlist) CRUD
// ============================================

export interface FavoriteRecord {
  id: string;
  college_id: number;
  college_name: string;
  status: string;
  student_id: string;
}

export type FavoriteStatus = "Interested" | "Done" | (string & {});

export type FavoriteStatusSummary = {
  totalFavorites: number;
  doneFavorites: number;
  allDone: boolean;
};

/**
 * Add a college to student's favorites (shortlist)
 */
export async function addToFavorites(
  authUserId: string,
  collegeId: string,
  collegeName: string,
): Promise<FavoriteRecord | null> {
  try {
    const collegeIdNumber = cleanCollegeId(collegeId);
    const studentId = await getStudentIdFromAuth(authUserId);

    // Check if favorite already exists
    const { data: existing } = await supabase
      .from("student_shortlists")
      .select("id")
      .eq("student_id", studentId)
      .eq("college_id", collegeIdNumber)
      .maybeSingle();

    if (existing) {
      return existing as FavoriteRecord;
    }

    const insertData = {
      college_id: collegeIdNumber,
      college_name: collegeName,
      status: "Interested",
      student_id: studentId,
    };

    const { data, error } = await supabase
      .from("student_shortlists")
      .insert(insertData)
      .select()
      .single();

    if (error) {
      // Ignore duplicate key errors
      if (error.code === "23505" || error.message?.includes("duplicate")) {
        return null;
      }
      console.error("Error adding to favorites:", error);
      throw new Error(`Failed to add to favorites: ${error.message}`);
    }

    return data as FavoriteRecord;
  } catch (error: unknown) {
    console.error("Error in addToFavorites:", getErrorMessage(error));
    throw error;
  }
}

/**
 * Remove a college from student's favorites (shortlist)
 */
export async function removeFromFavorites(
  authUserId: string,
  collegeId: string,
): Promise<void> {
  try {
    const collegeIdNumber = cleanCollegeId(collegeId);
    const studentId = await getStudentIdFromAuth(authUserId);

    const { data, error } = await supabase
      .from("student_shortlists")
      .delete()
      .eq("student_id", studentId)
      .eq("college_id", collegeIdNumber)
      .select();

    if (error) {
      console.error("Error removing from favorites:", error);
      throw new Error(`Failed to remove from favorites: ${error.message}`);
    }
  } catch (error: unknown) {
    console.error("Error in removeFromFavorites:", getErrorMessage(error));
    throw error;
  }
}

/**
 * Get all favorite college IDs for a student
 */
export async function getFavorites(authUserId: string): Promise<string[]> {
  try {
    const studentId = await getStudentIdFromAuth(authUserId);

    const { data, error } = await supabase
      .from("student_shortlists")
      .select("college_id")
      .eq("student_id", studentId);

    if (error) {
      console.error("Error getting favorites:", error);
      throw new Error(`Failed to get favorites: ${error.message}`);
    }

    return (data || []).map((item) => String(item.college_id));
  } catch (error: unknown) {
    console.error("Error in getFavorites:", getErrorMessage(error));
    throw error;
  }
}

/**
 * Get favorite statuses for a student (by college).
 * Returns a map keyed by college_id (string).
 */
export async function getFavoriteStatuses(
  authUserId: string,
  collegeIds?: string[],
): Promise<Record<string, FavoriteStatus>> {
  try {
    const studentId = await getStudentIdFromAuth(authUserId);

    let query = supabase
      .from("student_shortlists")
      .select("college_id, status")
      .eq("student_id", studentId);

    if (Array.isArray(collegeIds) && collegeIds.length > 0) {
      const numericIds = collegeIds
        .map((id) => {
          try {
            return cleanCollegeId(String(id));
          } catch {
            return null;
          }
        })
        .filter((n): n is number => typeof n === "number");

      if (numericIds.length > 0) {
        query = query.in("college_id", numericIds);
      }
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error getting favorite statuses:", error);
      throw new Error(`Failed to get favorite statuses: ${error.message}`);
    }

    const map: Record<string, FavoriteStatus> = {};
    for (const row of (data || []) as Array<{
      college_id: unknown;
      status?: unknown;
    }>) {
      const id =
        typeof row.college_id === "number"
          ? String(row.college_id)
          : typeof row.college_id === "string"
            ? row.college_id
            : "";
      if (!id) continue;
      const status =
        typeof row.status === "string" && row.status.trim()
          ? (row.status.trim() as FavoriteStatus)
          : ("Interested" as FavoriteStatus);
      map[id] = status;
    }

    return map;
  } catch (error: unknown) {
    console.error("Error in getFavoriteStatuses:", getErrorMessage(error));
    throw error;
  }
}

/**
 * Update favorite status for a single college.
 * Uses student_shortlists.status (existing column) to avoid schema changes.
 */
export async function updateFavoriteStatus(
  authUserId: string,
  collegeId: string,
  status: FavoriteStatus,
): Promise<void> {
  try {
    const collegeIdNumber = cleanCollegeId(collegeId);
    const studentId = await getStudentIdFromAuth(authUserId);

    const { data, error } = await supabase
      .from("student_shortlists")
      .update({ status })
      .eq("student_id", studentId)
      .eq("college_id", collegeIdNumber)
      .select("id");

    if (error) {
      console.error("Error updating favorite status:", error);
      throw new Error(`Failed to update favorite status: ${error.message}`);
    }

    // With RLS, an update that matches 0 rows can appear as "success" if no rows are visible/updatable.
    if (!data || (Array.isArray(data) && data.length === 0)) {
      throw new Error(
        "Favorite status was not updated (0 rows affected). Check RLS UPDATE policy on student_shortlists.",
      );
    }
  } catch (error: unknown) {
    console.error("Error in updateFavoriteStatus:", getErrorMessage(error));
    throw error;
  }
}

/**
 * Mark all favorites as a given status for the student.
 * Used for the student-side "Favorites finalized" toggle.
 */
export async function setAllFavoriteStatuses(
  authUserId: string,
  status: FavoriteStatus,
): Promise<void> {
  try {
    const studentId = await getStudentIdFromAuth(authUserId);

    // Count favorites first so we can detect silent no-op updates under RLS.
    // (Some PostgREST setups return `count: null` with head requests, so keep it simple.)
    const { data: existing, error: countError } = await supabase
      .from("student_shortlists")
      .select("id")
      .eq("student_id", studentId);

    if (countError) {
      console.error("Error counting favorites:", countError);
      throw new Error(`Failed to count favorites: ${countError.message}`);
    }
    const totalFavorites = (existing || []).length;

    const { data: updated, error } = await supabase
      .from("student_shortlists")
      .update({ status })
      .eq("student_id", studentId)
      .select("id");

    if (error) {
      console.error("Error updating all favorite statuses:", error);
      throw new Error(`Failed to update all favorite statuses: ${error.message}`);
    }

    // Note: PostgREST can return 0 affected rows with no error when RLS blocks updates.
    if (totalFavorites > 0) {
      if (!updated || updated.length === 0) {
        throw new Error(
          "Favorites were not updated (0 rows affected). Check RLS UPDATE policy on student_shortlists.",
        );
      }
    }
  } catch (error: unknown) {
    console.error("Error in setAllFavoriteStatuses:", getErrorMessage(error));
    throw error;
  }
}

/**
 * Get a simple summary of favorites status.
 */
export async function getFavoriteStatusSummary(
  authUserId: string,
): Promise<FavoriteStatusSummary> {
  try {
    const studentId = await getStudentIdFromAuth(authUserId);

    const { data, error } = await supabase
      .from("student_shortlists")
      .select("status")
      .eq("student_id", studentId);

    if (error) {
      console.error("Error getting favorite status summary:", error);
      throw new Error(`Failed to get favorite status summary: ${error.message}`);
    }

    const rows = (data || []) as Array<{ status?: unknown }>;
    const totalFavorites = rows.length;
    const doneFavorites = rows.filter(
      (r) => typeof r.status === "string" && r.status.toLowerCase() === "done",
    ).length;
    const allDone = totalFavorites > 0 && doneFavorites === totalFavorites;

    return { totalFavorites, doneFavorites, allDone };
  } catch (error: unknown) {
    console.error("Error in getFavoriteStatusSummary:", getErrorMessage(error));
    throw error;
  }
}

// ============================================
// Application Hearts (Dashboard Priority)
// ============================================

/**
 * Toggle "heart" status for an application (prioritized view on dashboard)
 */
export async function toggleApplicationHeart(
  authUserId: string,
  collegeId: string,
  applicationType: string = "RD",
): Promise<boolean> {
  try {
    const studentId = await getStudentIdFromAuth(authUserId);
    const numericId = parseInt(collegeId.replace(/\D/g, ""), 10);

    if (isNaN(numericId)) throw new Error("Invalid college ID");

    const safeType = (applicationType || "RD").toUpperCase();

    // Check if exists
    const { data: existing } = await supabase
      .from("application_hearts")
      .select("id")
      .eq("student_id", studentId)
      .eq("college_id", numericId)
      .eq("application_type", safeType)
      .maybeSingle();

    if (existing) {
      // Remove
      const { error } = await supabase
        .from("application_hearts")
        .delete()
        .eq("id", existing.id);

      if (error) throw error;
      return false;
    } else {
      // Add
      const { error } = await supabase.from("application_hearts").insert({
        student_id: studentId,
        college_id: numericId,
        application_type: safeType,
      });

      if (error) throw error;
      return true;
    }
  } catch (error: unknown) {
    console.error("Error toggling application heart:", getErrorMessage(error));
    throw error;
  }
}

/**
 * Get all hearted applications for a student
 */
export async function getApplicationHearts(
  authUserId: string,
): Promise<HeartedApplication[]> {
  try {
    const studentId = await getStudentIdFromAuth(authUserId);

    const { data, error } = await supabase
      .from("application_hearts")
      .select("college_id, application_type")
      .eq("student_id", studentId);

    if (error) throw error;

    return (data || []).map((row) => ({
      collegeId: String(row.college_id),
      applicationType: row.application_type || "RD",
    }));
  } catch (error: unknown) {
    console.error("Error getting application hearts:", getErrorMessage(error));
    return [];
  }
}
